import axios from 'axios';


let base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
if (base.endsWith('/api')) {
  // Don't add another /api if already present
  base = base.replace(/\/+$/, '');
  // Use as is
  // (no-op, just for clarity)
} else {
  base = base.replace(/\/+$/, '') + '/api';
}
const API_URL = base;

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getProfile: () => api.get('/users/profile'),
  getDashboard: () => api.get('/users/dashboard'),
  getMyPermissions: () => api.get('/users/my-permissions'),
  getRolePermissions: () => api.get('/users/role-permissions'),
  updateRolePermissions: (role, permissions) => api.put(`/users/role-permissions/${role}`, { permissions }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  deactivate: (id) => api.delete(`/users/${id}`),
};

// Resources API
export const resourcesAPI = {
  getAll: () => api.get('/resources'),
  create: (data) => api.post('/resources', data),
  update: (id, data) => api.put(`/resources/${id}`, data),
  delete: (id) => api.delete(`/resources/${id}`),
};

export const resourceTypesAPI = {
  getAll: () => api.get('/resource-types'),
  create: (data) => api.post('/resource-types', data),
  update: (id, data) => api.put(`/resource-types/${id}`, data),
  delete: (id) => api.delete(`/resource-types/${id}`),
};

// Teams API
export const teamsAPI = {
  getAll: () => api.get('/teams'),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  getMembers: (teamId) => api.get(`/teams/${teamId}/members`),
  addMember: (teamId, userId) => api.post(`/teams/${teamId}/members`, { userId }),
  removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
  getResources: (teamId) => api.get(`/teams/${teamId}/resources`),
  assignResource: (teamId, data) => api.post(`/teams/${teamId}/resources`, data),
  removeResource: (teamId, resourceId) => api.delete(`/teams/${teamId}/resources/${resourceId}`),
};

// Job Types API
export const jobTypesAPI = {
  getAll: () => api.get('/job-types'),
  create: (data) => api.post('/job-types', data),
  update: (id, data) => api.put(`/job-types/${id}`, data),
  delete: (id) => api.delete(`/job-types/${id}`),
};

// Tasks API
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Reports API
export const reportsAPI = {
  dailyReport: (date) => api.get(`/reports/daily/${date}`),
  monthlyReport: (year, month) => api.get(`/reports/monthly/${year}/${month}`),
  yearlyReport: (year) => api.get(`/reports/yearly/${year}`),
  attendanceReport: (params) => api.get('/reports/attendance', { params }),
  taskSummaryReport: (params) => api.get('/reports/tasks/summary', { params }),
  taskDetailReport: (params) => api.get('/reports/tasks/detail', { params }),
  userActivityReport: (params) => api.get('/reports/users/activity', { params }),
  resourceActivityReport: (params) => api.get('/reports/resources/activity', { params }),
  // Cost reports
  costResourceReport: (params) => api.get('/reports/costs/resources', { params }),
  costUserReport: (params) => api.get('/reports/costs/users', { params }),
  costTaskReport: (params) => api.get('/reports/costs/tasks', { params }),
  costSummary: () => api.get('/reports/costs/summary'),
  costTrend: (params) => api.get('/reports/costs/trend', { params }),
};

// Availability API
export const availabilityAPI = {
  getToday: () => api.get('/availability/today'),
  clockIn: (data = {}) => api.post('/availability/clock-in', data),
  clockOut: (data = {}) => api.post('/availability/clock-out', data),
  logs: () => api.get('/availability/logs'),
  setAvailability: (data) => api.post('/availability/set-availability', data),
  getPlanned: (userId) => api.get(`/availability/planned/${userId}`),
  cancelClockIn: (userId, date) => api.post('/availability/cancel-clock-in', { userId, date }),
  deleteClockEvent: (logId) => api.delete(`/availability/clock-events/${logId}`),
  deleteClockInEvent: (logId) => api.delete(`/availability/clock-events/${logId}`),
  getAvailabilityByDate: (date) => api.get(`/availability/all-users-by-date/${date}`),
  getCustomEvents: () => api.get('/availability/custom-events'),
  createCustomEvent: (data) => api.post('/availability/custom-events', data),
  updateCustomEvent: (eventId, data) => api.put(`/availability/custom-events/${eventId}`, data),
  deleteCustomEvent: (eventId) => api.delete(`/availability/custom-events/${eventId}`),
};

export const activityLogsAPI = {
  getAll: (limit = 20, days = null) =>
    api.get('/logs', { params: { limit, ...(days !== null ? { days } : {}) } }),
  clearAll: () => api.delete('/logs/clear'),
};

export const localizationAPI = {
  getAll: () => api.get('/localization'),
  createLanguage: (data) => api.post('/localization/languages', data),
  setDefaultLanguage: (code) => api.put('/localization/default-language', { code }),
  saveTranslations: (code, translations) => api.put(`/localization/translations/${code}`, { translations }),
};

export default api;
